import { ArrivalSlot } from './renderers/ArrivalSlot'
import { AuctionHouse } from './auctionHouse/AuctionHouse'
import { ContentHints } from './renderers/ContentHints'
import { ExecutionStatus, validate } from './wrapper'
import { GenericHints } from './renderers/GenericHints'
import { IndexExchange } from './partners/indexExchange/IndexExchange'
import { Interstitial } from './slots'
import { LiftTest } from './liftTest/LiftTest'
import { ListHints } from './renderers/ListHints'
import { Scroll } from './helpers/Scroll'
import { WindowSize } from './helpers'
import AdTags from './prebidFacade/videoBids/getAdTags'
import ATF from './renderers/Atf'
import Blocklist from './blocklist/Blocklist'
import Chicory from './chicory/Chicory'
import ComScore from './comScore/ComScore'
import Dynamic from './dynamic/Dynamic'
import DynamicRecipeSlots from './renderers/DynamicRecipeSlots'
import GoogletagFacade from './googletagFacade/GoogletagFacade'
import GrowFaves from './growFaves/GrowFaves'
import { Identity } from './identity/Identity'
import InjectableCreative from './injectableCreative/InjectableCreative'
import Keywords from './keywords/Keywords'
import Lazy from './renderers/Lazy'
import Logger from './helpers/Logger'
import PageIdLookup from './pageIdLookup/PageIdLookup'
import { OpenX } from './partners/openx/OpenX'
import GumGum from './partners/gumgum/GumGum'
import PrebidFacade from './prebidFacade/PrebidFacade'
import Refresh from './refresh/Refresh'
import SlotFactory from './slots/SlotFactory'
import { StickySlot } from './stickySlot/StickySlot'
import { Usersync } from './usersync/Usersync'
import type WebModel from './models/WebModel'
import Zergnet from './zergnet/Zergnet'
import { GeoLocation } from './helpers/geoLocation'
import { PnG } from './partners/PandG/PandG'
import { getAmazonAdjustments } from './helpers/amazonAdjustment'
import { SDA } from './usersync/SDA'

const debug = (msg: string) => Logger.custom(['debug', 'wrapper'], msg)

export const load = async (
  model: WebModel,
  blocklist: Blocklist,
  slotFactory: SlotFactory,
  gdpr: IGDPR,
  reRenderables: Array<{ render: Function }> = []
): Promise<ExecutionStatus> => {
  /** Render Validations */
  if (
    domainIsNotValid() ||
    siteIsDeactivated(model) ||
    siteIsKillswitched(model) ||
    windowIsTooSmall()
  ) {
    return ExecutionStatus.halted
  }

  const sda = new SDA(gdpr)
  const keywordService = new Keywords(sda)
  new PageIdLookup(model)

  // Don't move GeoLocation.retrieveGeo(). It sets the GeoLocation.geo static global.
  await Promise.all([
    GeoLocation.retrieveGeo().then((geoLoc) => {
      model.geoLoc = geoLoc
    }),
    PrebidFacade.loadPrebidImports(),
    // Test: Wait for grow to load before first auction
    GrowFaves.growHasLoaded.catch(() => {
      /** Don't care */
    })
  ])

  // intentionally don't await, treat
  // adjustments as best effot
  getAmazonAdjustments('v1', model)

  Identity.init(model)
  const liftTest = LiftTest.new(model)
  const auctionHouse = new AuctionHouse(model)
  slotFactory.registerSlotRenderEndedHandler(auctionHouse)
  const adTags = new AdTags(model, keywordService, liftTest, auctionHouse)

  window.$mediavine.web.getVideoAdTag = adTags.getAdTag.bind(adTags)
  window.$mediavine.web.getVideoAdTagAndHighBid =
    adTags.getAdTagAndHighBid.bind(adTags)
  window.$mediavine.web.getNextVideoAdTagAndHighBid =
    adTags.getNextAdTagAndHighBid.bind(adTags)
  window.$mediavine.web.auctionHouse = auctionHouse

  if (blocklist.all) {
    debug('Blocklist.all triggered. Halting execution.')
    // Footer branding is omitted from global blocklisting
    new ComScore(model)
    return ExecutionStatus.halted
  }

  gdpr.onReady(async () => {
    /**
     * Wait until the modal interaction is done before calling
     * GoogleTagFacade, as with TCF2 Google obtains the tcData on their own
     */
    new GoogletagFacade(model, auctionHouse, keywordService)

    // Disable personalized ads and usersync if the user is in Brazil
    if (model.geoLoc.country_code === 'BR') {
      GoogletagFacade.onReady(() => {
        window.googletag.pubads().setRequestNonPersonalizedAds(1)
      }).catch()
    } else {
      Usersync.init(model)
    }

    IndexExchange.init(model, blocklist)

    const prebidFacade = new PrebidFacade(model, gdpr, auctionHouse)
    Scroll.init()

    // If we know Grow is loading, we'll have deferred triggering the user syncs in the PrebidConfig.
    // Here, we'll manually trigger userSyncs after Grow has loaded or timed-out.
    if (GrowFaves.growIsLoading) {
      GrowFaves.growHasLoaded
        .catch((err) => {
          // don't care (probably timed out)
        })
        .finally(() => {
          Logger.debug('Triggering User Syncs')
          window.pbjs.triggerUserSyncs()

          // We want to disable the override so BidAdapter Syncing can function normally.
          window.pbjs.setConfig({
            userSync: {
              enableOverride: false
            }
          })
        })
    }

    // new Session(model.gdpr)
    // new UTM(model.gdpr)

    // Feature Modules
    new InjectableCreative(model)
    new StickySlot(model)
    const refresh = new Refresh(prebidFacade)
    new Chicory(model, blocklist)

    // Make all content slots lazyables
    Dynamic.attachPrebidFacade(prebidFacade)

    // Ad Renderers
    new Interstitial(model, blocklist, prebidFacade)
    const atf = new ATF(model, slotFactory, prebidFacade, blocklist)
    await atf.renderedPromise
    reRenderables.push(new Lazy(model, slotFactory, prebidFacade))
    reRenderables.push(new ListHints(prebidFacade, slotFactory))
    reRenderables.push(new ContentHints(prebidFacade, slotFactory))
    reRenderables.push(new GenericHints(prebidFacade, slotFactory))
    reRenderables.push(new ArrivalSlot(model, slotFactory, prebidFacade))
    reRenderables.push(new DynamicRecipeSlots(model, slotFactory))

    setInterval(() => reRenderables.forEach((r) => r.render()), 1000)

    // third-party partner modules
    new ComScore(model)
    new GumGum(model, blocklist, refresh)
    new Zergnet(model, blocklist)
    OpenX.insertESPscript(model)
    PnG.init({ model, gdpr })
  })

  return ExecutionStatus.executed
}

function domainIsNotValid(): boolean {
  const isNotValid =
    window.$mediavine.web.model.whitelistDomain &&
    !!window.$mediavine.web.model.domain &&
    window.$mediavine.web.model.domain !== window.location.hostname

  if (isNotValid) {
    debug('Domain not whitelisted. Halting Execution.')
  }

  return isNotValid
}

const siteIsDeactivated = (model): boolean =>
  validate(model.deactivated, 'Site has been deactivated.')
const siteIsKillswitched = (model): boolean =>
  validate(model.killswitch, 'Site killswitch is activated.')
const windowIsTooSmall = (): boolean =>
  validate(WindowSize.isWindowTooSmall(), 'Window is too small.')
